import React from 'react';
import { ArrowDownIcon, CheckIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { CheckoutButton } from '../CheckoutButton';
import { AUDIENCE, COSTS, NUMBERS, OFFER_ANCHOR, PAINS, SUPPORT_EMAIL, VOLUMES, WHY_TODAY } from '../../data/woodworking';
import { PRICE } from '../../data/checkout';
import { primaryButtonClass } from '../../utils/buttonStyles';

/** Audience chips + numbers strip + pain with a reframe and a real project photo. */
export function AudiencePainSection() {
  return <section aria-labelledby="pain-title" className="bg-ivory py-12 text-ink sm:py-16" data-id="element-192" data-mp="wzdsfd890vyc">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8" data-id="element-193" data-mp="11wm6b1md6dts">
        <ul className="flex flex-wrap justify-center gap-2" aria-label="Made for" data-id="element-194" data-mp="16p6zq21cyows">
          {AUDIENCE.map(a => <li key={a} className="rounded-full border border-forest/25 bg-forest-tint px-3 py-1 text-[0.8rem] font-semibold text-forest" data-id="element-195" data-mp="98qeobbnjc3v">
              {a}
            </li>)}
        </ul>
        <ul className="mt-6 grid grid-cols-4 gap-2 text-center" data-id="element-196" data-mp="1ble2kj51p8ab">
          {NUMBERS.map(n => <li key={n.label} className="rounded-[10px] bg-paper px-1 py-3 ring-1 ring-ivory-line" data-id="element-197" data-mp="16oxfls9l0zyt">
              <p className="font-brand text-[1.7rem] font-bold leading-none text-ember-deep sm:text-[2.2rem]" data-id="element-198" data-mp="11vl1wd6fydku">{n.value}</p>
              <p className="mt-1 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-stone sm:text-[0.8rem]" data-id="element-199" data-mp="xv2bmnk75fkr">{n.label}</p>
            </li>)}
        </ul>

        <div className="mt-10 grid items-center gap-8 lg:grid-cols-2" data-id="element-200" data-mp="nwd1xi2gf1fl">
          <div data-id="element-201" data-mp="ixi9doeopyia">
            <SectionHeading id="pain-title" eyebrow="Sound familiar?" title="A week in the shop. A table that goes home full." data-id="element-202" data-mp="iz33jcx37ffm" />
            <ul className="mt-5 grid gap-2.5" data-id="element-203" data-mp="ey9fmjjna0mq">
              {PAINS.map(p => <li key={p} className="flex items-start gap-2.5 text-[0.98rem] leading-snug text-ink" data-id="element-204" data-mp="2darihf3bji00">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-ember" strokeWidth={3} aria-hidden data-id="element-205" data-mp="29azap4lr1fvp" />
                  {p}
                </li>)}
            </ul>
            <p className="mt-6 rounded-[10px] border-l-4 border-brass bg-paper px-4 py-3 font-brand text-[1.15rem] font-bold leading-snug text-pine" data-id="element-206" data-mp="bfkeaox92pyl">
              It’s not a lack of skill. It’s building the wrong things without a complete plan.
            </p>
          </div>
          <img src={VOLUMES[1].cover.src} alt={VOLUMES[1].cover.alt} width={1280} height={1600} loading="lazy" decoding="async" className="mx-auto w-full max-w-[360px] rounded-[10px] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]" data-id="element-207" data-mp="pejtl5yvhsi3" />
        </div>
      </div>
    </section>;
}

/** Three simple steps. */
export function StepsSection() {
  const steps = [{
    n: '1',
    t: 'Choose',
    d: 'Pick the projects that fit your fair, your shop and your gift list.'
  }, {
    n: '2',
    t: 'Batch',
    d: 'Take the cut list to the lumber yard and cut a whole batch with stop blocks.'
  }, {
    n: '3',
    t: 'Sell or gift',
    d: 'Finish, tag and price each piece with its build & sell card.'
  }];
  return <section aria-labelledby="steps-title" className="bg-dune py-12 text-ink sm:py-16" data-id="element-208" data-mp="2tmo5beuwq1p2">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8" data-id="element-209" data-mp="37i0bcieijela">
        <SectionHeading id="steps-title" align="center" eyebrow="As simple as it gets" title="Choose. Batch. Sell." data-id="element-210" data-mp="32m1fyaurowbd" />
        <ol className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-211" data-mp="2evywgj4x4yod">
          {steps.map(s => <li key={s.n} className="rounded-[12px] bg-paper p-5 text-center ring-1 ring-ivory-line" data-id="element-212" data-mp="29xnmm8emio65">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-bottle font-brand text-[1.3rem] font-bold text-ivory" data-id="element-213" data-mp="2omth5j2inq7p">{s.n}</span>
              <h3 className="mt-3 font-brand text-[1.2rem] font-bold" data-id="element-214" data-mp="1w4eaw137dqkg">{s.t}</h3>
              <p className="mt-1.5 text-[0.95rem] leading-relaxed text-stone" data-id="element-215" data-mp="1r5ut8cvn70xw">{s.d}</p>
            </li>)}
        </ol>
        <div className="mt-8 flex justify-center" data-id="element-216" data-mp="uf95fhezbvnp">
          <a href={OFFER_ANCHOR} className={primaryButtonClass('hero')} data-id="element-217" data-mp="ucd89vayhb0b">
            <span data-id="element-218" data-mp="ze2gl36snakp">GET ALL 30 PROJECTS + 5 BONUSES</span>
            <ArrowDownIcon className="hidden h-[18px] w-[18px] shrink-0 sm:block" strokeWidth={2.5} aria-hidden data-id="element-219" data-mp="13emewa33sk8f" />
          </a>
        </div>
      </div>
    </section>;
}

/** Big 7-day guarantee (new landings, 31/10). */
export function GuaranteeSection() {
  return <section id="guarantee" aria-labelledby="guarantee-title" className="bg-ivory py-14 text-ink sm:py-20" data-id="element-220" data-mp="1d7bnmx79rk02">
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-5 text-center sm:px-8" data-id="element-221" data-mp="1n359x8asirv1">
        <div className="flex h-36 w-36 flex-col items-center justify-center rounded-full border-[6px] border-double border-brass bg-paper shadow-[0_20px_40px_-20px_rgba(0,0,0,0.5)]" data-id="element-222" data-mp="1r4qg6astk7w9">
          <ShieldCheckIcon className="h-9 w-9 text-forest" aria-hidden data-id="element-223" data-mp="3owkxtxfhtsmb" />
          <p className="mt-1 font-brand text-[2rem] font-bold leading-none text-pine" data-id="element-224" data-mp="3swlp4inoxyas">7 DAYS</p>
          <p className="mt-1 text-[0.68rem] font-bold uppercase tracking-[0.12em] text-brass" data-id="element-225" data-mp="3le5ep47wjktb">100% money back</p>
        </div>
        <h2 id="guarantee-title" className="mt-6 text-balance font-brand text-[1.9rem] font-bold leading-tight text-ink sm:text-[2.4rem]" data-id="element-226" data-mp="3gjrg6sxy0xkf">
          7 days, no questions asked
        </h2>
        <p className="mt-4 max-w-xl text-[1.02rem] leading-relaxed text-stone" data-id="element-227" data-mp="37n0lokz1hqry">
          Download everything, open the plans, pick your first project. If the collection is not what you expected, email{' '}
          <span className="font-semibold text-ink" data-id="element-228" data-mp="34v1ndm1ug4w">{SUPPORT_EMAIL}</span> within 7 days and we refund 100% of your payment. The risk is ours.
        </p>
      </div>
    </section>;
}

/** Cost of not having it + why today + emotional CTA. */
export function CostSection() {
  return <section aria-labelledby="cost-title" className="bg-cream py-14 text-ink sm:py-20" data-id="element-229" data-mp="2jwwk0j5ew13l">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8" data-id="element-230" data-mp="2euxf34u0ww4j">
        <SectionHeading id="cost-title" align="center" eyebrow="Think about it" title="What does NOT having it cost you?" data-id="element-231" data-mp="2gd5wbnwjwhif" />
        <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-232" data-mp="2lbfqbv6jhc2g">
          {COSTS.map(c => <li key={c.title} className="rounded-[12px] bg-paper p-5 ring-1 ring-ivory-line" data-id="element-233" data-mp="27bxwh1edz8m8">
              <h3 className="font-brand text-[1.15rem] font-bold text-ember-deep" data-id="element-234" data-mp="2bdt7wyq172dq">{c.title}</h3>
              <p className="mt-1.5 text-[0.95rem] leading-relaxed text-stone" data-id="element-235" data-mp="1xmwgvcbr6t8s">{c.text}</p>
            </li>)}
        </ul>
        <div className="mt-10 rounded-[14px] bg-bottle p-6 text-ivory sm:p-8" data-id="element-236" data-mp="22fh7k4zd4q83">
          <p className="label-tech text-brass-light" data-id="element-237" data-mp="1nq1jiwr7uyw3">Why today?</p>
          <ul className="mt-3 grid gap-2" data-id="element-238" data-mp="1sntnip54ybgn">
            {WHY_TODAY.map(w => <li key={w} className="flex items-start gap-2.5 text-[0.98rem] leading-snug" data-id="element-239" data-mp="1slqlf6qpkrvk">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brass-light" strokeWidth={3} aria-hidden data-id="element-240" data-mp="1eqo9a1v9q6ab" />
                {w}
              </li>)}
          </ul>
          <CheckoutButton label={`YES, I WANT ALL 30 PROJECTS — ${PRICE.label}`} size="lg" className="mt-6 w-full" data-id="element-241" data-mp="1024yzejglhdk" />
          <p className="mt-3 text-center text-[0.82rem] text-parchment" data-id="element-242" data-mp="v4mrztp05c87">7-day money-back guarantee · Instant download</p>
        </div>
      </div>
    </section>;
}